// Pages/UserAccount/Layout/UserAccountGridLayout.jsx
import React from 'react';
import { fetchUserData, fetchUserByIdOnly, extractMediaByType } from '../Utils/fetchUserData';
import ImageModal from '../Utils/ImageModal';
import '../Styles/UserAccountGridLayout.css';

// ---------------------------------------------------------------------------
// Inline SVG icons (no external icon library)
// ---------------------------------------------------------------------------
function MapPinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function ImagePlaceholderIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// GridCard
// ---------------------------------------------------------------------------
function GridCard({ item, onClick }) {
  const isVideo =
    item.mediatype &&
    (item.mediatype.startsWith('video/') || item.mediatype === 'reel');

  return (
    <div className="uag-card" onClick={() => onClick(item)} role="button" tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(item)}>
      <div className="uag-card__media-wrapper">
        {isVideo ? (
          <video
            className="uag-card__media"
            src={item.mediaurl}
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <img
            className="uag-card__media"
            src={item.mediaurl}
            alt={item.description || 'media'}
            loading="lazy"
          />
        )}
        <div className="uag-card__overlay">
          <span className="uag-card__overlay-stat">
            <EyeIcon /> {item.impressions}
          </span>
        </div>
      </div>

      <div className="uag-card__meta">
        <div className="uag-card__meta-row">
          <UserIcon />
          <span className="uag-card__meta-text">{item.posted_by}</span>
        </div>
        {item.location && (
          <div className="uag-card__meta-row">
            <MapPinIcon />
            <span className="uag-card__meta-text">{item.location}</span>
          </div>
        )}
        {item.timestamp && (
          <div className="uag-card__meta-row">
            <ClockIcon />
            <span className="uag-card__meta-text">{item.timestamp}</span>
          </div>
        )}
        {item.description && (
          <p className="uag-card__description">{item.description}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserAccountGridLayout
// ---------------------------------------------------------------------------
function UserAccountGridLayout({ fetchMode, userData, contentType, onDataLoaded }) {
  const [items, setItems]           = React.useState([]);
  const [loading, setLoading]       = React.useState(false);
  const [error, setError]           = React.useState(null);
  const [selectedItem, setSelectedItem] = React.useState(null);

  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    setError(null);
    setItems([]);

    let fetchPromise;

    if (fetchMode === 'suserid-tuserid') {
      fetchPromise = fetchUserData(userData.suserid, userData.tuserid);
    } else if (fetchMode === 'tuserid-only') {
      fetchPromise = fetchUserByIdOnly(userData.tuserid);
    } else {
      setError('Unknown fetch mode');
      setLoading(false);
      return;
    }

    fetchPromise
      .then((formattedData) => {
        if (cancelledRef.current) return;
        const extracted = extractMediaByType(formattedData, contentType);
        setItems(extracted);
        if (typeof onDataLoaded === 'function') {
          onDataLoaded(formattedData);
        }
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        setError(err.message || 'Failed to load user data');
      })
      .finally(() => {
        if (!cancelledRef.current) setLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [fetchMode, userData?.suserid, userData?.tuserid, contentType]);

  // ---------- loading ----------
  if (loading) {
    return (
      <div className="uag-state uag-state--loading">
        <div className="uag-spinner" />
        <p className="uag-state__text">Loading {contentType}…</p>
      </div>
    );
  }

  // ---------- error ----------
  if (error) {
    return (
      <div className="uag-state uag-state--error">
        <div className="uag-state__icon uag-state__icon--error">
          <ImagePlaceholderIcon />
        </div>
        <p className="uag-state__text uag-state__text--error">
          Could not load {contentType}
        </p>
        <p className="uag-state__subtext">{error}</p>
      </div>
    );
  }

  // ---------- empty ----------
  if (items.length === 0) {
    return (
      <div className="uag-state uag-state--empty">
        <div className="uag-state__icon">
          <ImagePlaceholderIcon />
        </div>
        <p className="uag-state__text">No {contentType} available for this user</p>
      </div>
    );
  }

  // ---------- grid ----------
  return (
    <div className="uag-grid-root">
      <div className="uag-grid">
        {items.map((item, index) => (
          <GridCard
            // Fallback to index-based key when item.id is null/missing,
            // so React never sees duplicate `null` keys across siblings.
            key={item.id ?? `uag-card-${index}`}
            item={item}
            onClick={setSelectedItem}
          />
        ))}
      </div>

      {selectedItem && (
        <ImageModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}

export default UserAccountGridLayout;
